import { useState } from 'react'
import { errorMessage } from '../../api/client'
import { api } from '../../api/endpoints'
import type { AccessReason } from '../../api/types'
import { Dialog } from '../../components/Dialog'
import { Icon } from '../../components/Icon'

/** The company's reasons for reading someone else's conversation. Anything outside this list is not one. */
const REASONS: { id: AccessReason; label: string }[] = [
  { id: 'business', label: 'Business' },
  { id: 'hr', label: 'HR' },
  { id: 'legal', label: 'Legal' },
  { id: 'safeguarding', label: 'Safeguarding' },
  { id: 'security', label: 'Security' },
]

/** Long enough to say which case; the server asks for the same. */
const MIN_NOTE = 10

/**
 * Before an administrator reads a conversation they are not in. It says what is about to be written down, asks what the
 * reading is for, and does nothing at all until that is answered: closing it reads nothing, so nothing is recorded.
 */
export function AdminAccessDialog({
  channelId,
  name,
  onOpened,
  onCancel,
}: {
  channelId: string
  name: string
  onOpened: () => void
  onCancel: () => void
}) {
  const [reason, setReason] = useState<AccessReason | ''>('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ready = reason !== '' && note.trim().length >= MIN_NOTE

  const open = async () => {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      await api.openAdminAccess(channelId, reason as AccessReason, note.trim())
      onOpened()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      chrome="bar"
      open
      onClose={onCancel}
      title="Open as admin"
      titleId="admin-access-title"
      description={`Say why you are opening ${name}, a conversation you are not in.`}
      className="new-dialog"
      action={
        <button type="submit" form="admin-access-form" className="bar-button bar-action" disabled={busy || !ready}>
          Open
        </button>
      }
    >
      <form
        id="admin-access-form"
        style={{ display: 'contents' }}
        onSubmit={(e) => {
          e.preventDefault()
          void open()
        }}
      >
        <div className="dialog-body">
          {error ? (
            <p role="alert" className="field-error form-error">
              {error}
            </p>
          ) : null}
          <p className="policy-notice" role="note">
            <Icon name="shield" />
            <span>
              <strong>{name}</strong> — you are not in this conversation. Opening it is recorded in the audit log: your name, this conversation, the date
              and time, and the reason you give here.
            </span>
          </p>
          <div className="field">
            <label className="field-label" htmlFor="access-reason">
              Reason
            </label>
            <select id="access-reason" data-autofocus className="select-input" value={reason} onChange={(e) => setReason(e.target.value as AccessReason)} required>
              <option value="">Choose a reason</option>
              {REASONS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="access-note">
              What this is for
            </label>
            <textarea
              id="access-note"
              className="text-input"
              rows={3}
              maxLength={500}
              value={note}
              placeholder="e.g. Grievance raised on Tuesday"
              onChange={(e) => setNote(e.target.value)}
              required
            />
            <p className="field-help">At least {MIN_NOTE} characters. Covers this conversation for 4 hours.</p>
          </div>
          <button type="submit" className="button primary" disabled={busy || !ready}>
            Open and log access
          </button>
        </div>
      </form>
    </Dialog>
  )
}
